import { AlertCircle, ArrowLeft, CheckCircle2, ChevronDown, ChevronRight, Clock, ExternalLink, Loader2, Users, Zap } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import BONavigation from "components/admin/BONavigation";
import {
    getEstablishmentEnrichments,
    EnrichmentEntry,
    EnrichmentEvent,
    EnrichmentStatusValue,
    EstablishmentEnrichmentsResponse,
} from "interfaces/establishmentEnrichments";

// ─── helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// Green = finished, red = failed, gray = everything still in the pipeline
const STATUS_META: Record<EnrichmentStatusValue, { label: string; cls: string }> = {
    pending:          { label: "En attente",        cls: "bg-gray-100 text-gray-600" },
    processing:       { label: "En cours",          cls: "bg-gray-100 text-gray-900" },
    waiting_callback: { label: "Attente callback",  cls: "bg-gray-100 text-gray-900" },
    retry:            { label: "À relancer",        cls: "bg-gray-100 text-gray-600" },
    unsure:           { label: "Incertain",         cls: "bg-gray-100 text-gray-600" },
    done:             { label: "Terminé",           cls: "bg-emerald-50 text-emerald-700" },
    approved:         { label: "Approuvé",          cls: "bg-emerald-50 text-emerald-700" },
    closed:           { label: "Clos",              cls: "bg-gray-100 text-gray-500" },
    error:            { label: "Erreur",            cls: "bg-red-50 text-red-600" },
    rejected:         { label: "Rejeté",            cls: "bg-red-50 text-red-600" },
};

const STAGE_LABEL: Record<string, string> = {
    raw: "Brut",
    deduplicated: "Dédupliqué",
    enriched_partial: "Enrichi partiellement",
    enriched_full: "Enrichi",
    scored: "Scoré",
    completed: "Terminé",
    error: "Erreur",
};

const SUMMARY_ORDER: EnrichmentStatusValue[] = ["approved", "done", "waiting_callback", "processing", "pending", "retry", "unsure", "closed", "error", "rejected"];

function StatusBadge({ status }: { status: EnrichmentStatusValue }) {
    const meta = STATUS_META[status] ?? { label: status, cls: "bg-gray-100 text-gray-600" };
    const spinning = status === "processing" || status === "waiting_callback";
    return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${meta.cls}`}>
            {spinning && <Loader2 className="w-3 h-3 animate-spin" />}
            {meta.label}
        </span>
    );
}

function countStatuses(entries: EnrichmentEntry[]): Partial<Record<EnrichmentStatusValue, number>> {
    const counts: Partial<Record<EnrichmentStatusValue, number>> = {};
    for (const e of entries) counts[e.status] = (counts[e.status] ?? 0) + 1;
    return counts;
}

// ─── enrichment table ─────────────────────────────────────────────────────────

function EnrichmentRow({ entry }: { entry: EnrichmentEntry }) {
    const [open, setOpen] = useState(false);
    const hasDetails = !!(entry.error || entry.blockedReason || entry.dataPreview || entry.appliedPaths.length || entry.optionsCount);

    return (
        <>
            <tr
                onClick={() => hasDetails && setOpen((o) => !o)}
                className={`border-t border-gray-100 ${hasDetails ? "cursor-pointer hover:bg-[#F9F6F3]" : ""}`}
            >
                <td className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900">
                        {hasDetails
                            ? open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />
                            : <span className="w-4 h-4" />}
                        {entry.type}
                    </span>
                </td>
                <td className="px-4 py-2.5"><StatusBadge status={entry.status} /></td>
                <td className="px-4 py-2.5 text-xs text-gray-500">{formatDate(entry.lastRunAt)}</td>
                <td className="px-4 py-2.5 text-xs text-gray-500">
                    {entry.reviewedAt
                        ? `${entry.isManuallyReviewed ? "Manuel" : "Auto"} · ${formatDate(entry.reviewedAt)}`
                        : "—"}
                </td>
                <td className="px-4 py-2.5 text-xs text-gray-500">
                    <div className="flex flex-wrap items-center gap-1.5">
                        {entry.retryCount > 0 && <span>{entry.retryCount} essai{entry.retryCount > 1 ? "s" : ""}</span>}
                        {entry.optionsCount > 0 && <span>{entry.optionsCount} option{entry.optionsCount > 1 ? "s" : ""}</span>}
                        {entry.aiConfidenceLevel && entry.aiConfidenceLevel !== "unverified" && <span>IA : {entry.aiConfidenceLevel}</span>}
                        {entry.appliedToEstablishment && <span className="text-emerald-700">Appliqué</span>}
                        {entry.blockedReason && <span className="text-red-600">Bloqué</span>}
                        {entry.error && <AlertCircle className="w-4 h-4 text-red-600" />}
                    </div>
                </td>
            </tr>
            {open && (
                <tr className="bg-[#F9F6F3]">
                    <td colSpan={5} className="px-4 py-3 space-y-2">
                        {entry.error && (
                            <p className="text-xs text-red-600"><span className="font-semibold">Erreur :</span> {entry.error}</p>
                        )}
                        {entry.blockedReason && (
                            <p className="text-xs text-red-600">
                                <span className="font-semibold">Bloqué :</span> {entry.blockedReason}
                                {entry.blockedAt && <span className="text-gray-500"> · {formatDate(entry.blockedAt)}</span>}
                            </p>
                        )}
                        {entry.appliedPaths.length > 0 && (
                            <p className="text-xs text-gray-600">
                                <span className="font-semibold">Champs appliqués :</span> {entry.appliedPaths.join(", ")}
                            </p>
                        )}
                        {entry.dataPreview && (
                            <pre className="max-h-80 overflow-auto rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-700 whitespace-pre-wrap break-all">
                                {entry.dataPreview}
                            </pre>
                        )}
                    </td>
                </tr>
            )}
        </>
    );
}

function EnrichmentTable({ entries }: { entries: EnrichmentEntry[] }) {
    if (!entries.length) return <p className="px-4 py-3 text-xs text-gray-500">Aucun enrichissement.</p>;
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left">
                <thead>
                    <tr className="text-xs text-gray-500">
                        <th className="px-4 py-2 font-medium">Type</th>
                        <th className="px-4 py-2 font-medium">Statut</th>
                        <th className="px-4 py-2 font-medium">Dernière exécution</th>
                        <th className="px-4 py-2 font-medium">Revue</th>
                        <th className="px-4 py-2 font-medium">Infos</th>
                    </tr>
                </thead>
                <tbody>
                    {entries.map((e, i) => <EnrichmentRow key={`${e.type}-${i}`} entry={e} />)}
                </tbody>
            </table>
        </div>
    );
}

// ─── event card ───────────────────────────────────────────────────────────────

function EventCard({ event }: { event: EnrichmentEvent }) {
    const [peopleOpen, setPeopleOpen] = useState(true);
    const counts = countStatuses(event.enrichments);

    return (
        <section className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <header className="flex flex-wrap items-start justify-between gap-3 px-4 py-4 border-b border-gray-100">
                <div className="min-w-0">
                    <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
                        <Zap className="w-5 h-5 text-gray-400" />
                        {event.companyName}
                    </h2>
                    <p className="mt-1 text-xs text-gray-500">
                        {event.source}
                        {event.eventType && ` · ${event.eventType}`}
                        {` · détecté le ${formatDate(event.detectedAt)}`}
                        {event.naf_code && ` · NAF ${event.naf_code}`}
                        <span className="text-gray-400"> · {event._id}</span>
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                    {event.stage && (
                        <span className="rounded-full border border-gray-200 px-2 py-0.5 text-xs text-gray-600">
                            {STAGE_LABEL[event.stage] ?? event.stage}
                        </span>
                    )}
                    {SUMMARY_ORDER.filter((s) => counts[s]).map((s) => (
                        <span key={s} className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_META[s].cls}`}>
                            {counts[s]} {STATUS_META[s].label.toLowerCase()}
                        </span>
                    ))}
                </div>
            </header>

            <EnrichmentTable entries={event.enrichments} />

            {event.notStarted.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 border-t border-gray-100 px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="w-4 h-4" />
                        Règles actives pas encore lancées :
                    </span>
                    {event.notStarted.map((t) => (
                        <span key={t} className="rounded-full border border-dashed border-gray-300 px-2 py-0.5 text-xs text-gray-500">{t}</span>
                    ))}
                </div>
            )}

            <div className="border-t border-gray-100">
                <button
                    onClick={() => setPeopleOpen((o) => !o)}
                    className="flex w-full items-center gap-1.5 px-4 py-3 text-sm font-medium text-gray-900 hover:bg-[#F9F6F3]"
                >
                    {peopleOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <Users className="w-4 h-4 text-gray-400" />
                    Personnes liées ({event.people.length})
                </button>
                {peopleOpen && (
                    event.people.length === 0 ? (
                        <p className="px-4 pb-3 text-xs text-gray-500">Aucune personne rattachée à cet event.</p>
                    ) : (
                        <div className="space-y-3 px-4 pb-4">
                            {event.people.map((p) => (
                                <div key={p._id} className="rounded-lg border border-gray-200">
                                    <p className="px-4 pt-3 text-sm font-medium text-gray-900">
                                        {p.name}
                                        {p.qualite && <span className="font-normal text-gray-500"> · {p.qualite}</span>}
                                    </p>
                                    <EnrichmentTable entries={p.enrichments} />
                                </div>
                            ))}
                        </div>
                    )
                )}
            </div>
        </section>
    );
}

// ─── main ─────────────────────────────────────────────────────────────────────

export default function EstablishmentEnrichments() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [data, setData] = useState<EstablishmentEnrichmentsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        setError(null);
        getEstablishmentEnrichments(id)
            .then(setData)
            .catch((err) => setError(err?.response?.status === 404 ? "Établissement introuvable" : "Impossible de charger les enrichissements"))
            .finally(() => setLoading(false));
    }, [id]);

    const totals = useMemo(() => {
        const all = (data?.events ?? []).flatMap((ev) => [...ev.enrichments, ...ev.people.flatMap((p) => p.enrichments)]);
        const counts = countStatuses(all);
        const finished = (counts.done ?? 0) + (counts.approved ?? 0);
        const failed = (counts.error ?? 0) + (counts.rejected ?? 0);
        return { total: all.length, finished, failed, inProgress: all.length - finished - failed };
    }, [data]);

    const est = data?.establishment;

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-5xl px-4 py-6">
                <button onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 mb-5 transition-colors">
                    <ArrowLeft className="w-4 h-4" />
                    Retour
                </button>

                <BONavigation />

                {loading ? (
                    <div className="animate-pulse space-y-4 mt-4">
                        <div className="h-6 bg-gray-200 rounded-full w-1/3" />
                        <div className="h-4 bg-gray-100 rounded-full w-1/2" />
                    </div>
                ) : error || !data || !est ? (
                    <p className="mt-8 text-center text-sm text-gray-500">{error ?? "Établissement introuvable"}</p>
                ) : (
                    <>
                        {/* header */}
                        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                            <div className="min-w-0">
                                <h1 className="text-2xl font-semibold text-gray-900">{est.name}</h1>
                                <p className="mt-1 text-sm text-gray-500">
                                    {[est.legal_name, [est.address?.postcode, est.address?.city].filter(Boolean).join(" ")].filter(Boolean).join(" · ")}
                                </p>
                                <p className="mt-1 text-xs text-gray-500">
                                    SIRET {est.registration?.siret ?? "—"} · SIREN {est.registration?.siren ?? "—"}
                                    {est.operations?.opening_date && ` · ouverture ${new Date(est.operations.opening_date).toLocaleDateString("fr-FR")}`}
                                </p>
                            </div>
                            <Link to={`/establishments/${est._id}`} target="_blank"
                                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-900 hover:shadow-sm">
                                <ExternalLink className="w-4 h-4" />
                                Voir la fiche
                            </Link>
                        </div>

                        {/* summary */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                            {[
                                { label: "Events liés", value: data.events.length, cls: "text-gray-900" },
                                { label: "Terminés", value: totals.finished, cls: "text-emerald-700" },
                                { label: "En cours / en attente", value: totals.inProgress, cls: "text-gray-900" },
                                { label: "Erreurs / rejets", value: totals.failed, cls: totals.failed ? "text-red-600" : "text-gray-900" },
                            ].map((s) => (
                                <div key={s.label} className="bg-white border border-gray-200 rounded-xl p-4">
                                    <p className="text-xs text-gray-500 mb-1">{s.label}</p>
                                    <p className={`text-2xl font-semibold ${s.cls}`}>{s.value}</p>
                                </div>
                            ))}
                        </div>

                        {data.matchedBy === "siren" && (
                            <p className="mb-4 inline-flex items-center gap-1.5 text-xs text-gray-500">
                                <AlertCircle className="w-4 h-4" />
                                Aucun event ne porte ce SIRET : events rattachés par SIREN (sans SIRET résolu).
                            </p>
                        )}

                        {data.events.length === 0 ? (
                            <div className="bg-white border border-gray-200 rounded-xl px-4 py-10 text-center">
                                <p className="text-sm text-gray-900">Aucun event lié à cet établissement</p>
                                <p className="mt-1 text-xs text-gray-500">
                                    Le lien se fait par le SIRET (ou à défaut le SIREN) trouvé par l'enrichissement « siren » de l'event.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {data.events.map((ev) => <EventCard key={ev._id} event={ev} />)}
                            </div>
                        )}

                        {/* rules */}
                        {data.rules.length > 0 && (
                            <section className="mt-6 bg-white border border-gray-200 rounded-xl px-4 py-4">
                                <h2 className="text-base font-semibold text-gray-900 mb-3">Règles d'enrichissement</h2>
                                <div className="flex flex-wrap gap-1.5">
                                    {data.rules.map((r) => (
                                        <span key={r.type} title={r.description ?? undefined}
                                            className={`inline-flex items-center gap-1 rounded-full border border-gray-200 px-2 py-0.5 text-xs ${r.enabled ? "text-gray-900" : "text-gray-400 line-through"}`}>
                                            {r.enabled && <CheckCircle2 className="w-3 h-3 text-emerald-700" />}
                                            {r.type}
                                            <span className="text-gray-400">{r.targetModel === "Person" ? "personne" : "event"}</span>
                                        </span>
                                    ))}
                                </div>
                            </section>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
